import profileService from '../../../services/profile-service'
import { SetToast } from '../toaster'

// Action types
export const SET_PROFILE_ICON_UPLOADING = 'SET_PROFILE_ICON_UPLOADING'
export const SET_PROFILE_ICON_UPLOAD_PROGRESS = 'SET_PROFILE_ICON_UPLOAD_PROGRESS'
export const SET_PROFILE_ICON_URL = 'SET_PROFILE_ICON_URL'
export const SET_PROFILE_ICON_ERROR = 'SET_PROFILE_ICON_ERROR'
export const RESET_PROFILE_ICON_UPLOAD_STATE = 'RESET_PROFILE_ICON_UPLOAD_STATE'

// Action creators
export const setProfileIconUploading = (isUploading) => dispatch => {
	dispatch({ type: SET_PROFILE_ICON_UPLOADING, data: isUploading })
}

export const setProfileIconUploadProgress = (progress) => dispatch => {
	dispatch({ type: SET_PROFILE_ICON_UPLOAD_PROGRESS, data: progress })
}

export const setProfileIconUrl = (iconUrl) => dispatch => {
	dispatch({ type: SET_PROFILE_ICON_URL, data: iconUrl })
}

export const setProfileIconError = (error) => dispatch => {
	dispatch({ type: SET_PROFILE_ICON_ERROR, data: error })
}

export const resetProfileIconUploadState = () => dispatch => {
	dispatch({ type: RESET_PROFILE_ICON_UPLOAD_STATE })
}

/**
 * Upload profile icon
 * @param {number} profileId - The profile ID
 * @param {File} file - The image file to upload
 * @param {AbortController} abortController - Optional abort controller for cancellation
 */
export const uploadProfileIcon = (profileId, file, abortController) => async (dispatch) => {
	try {
		// Reset error state
		dispatch({ type: SET_PROFILE_ICON_ERROR, data: null })
		dispatch({ type: SET_PROFILE_ICON_UPLOADING, data: true })
		dispatch({ type: SET_PROFILE_ICON_UPLOAD_PROGRESS, data: 0 })

		// Call the upload service
		const response = await profileService.uploadProfileIcon(
			profileId,
			file,
			(progress) => {
				dispatch({ type: SET_PROFILE_ICON_UPLOAD_PROGRESS, data: progress })
			},
			abortController
		)

		if (response?.data?.iconUrl) {
			// Update profile icon URL in Redux
			dispatch({ type: SET_PROFILE_ICON_URL, data: response.data.iconUrl })

			// Show success message
			dispatch(SetToast({
				title: response.data.message || 'Profile icon uploaded successfully',
				show: true,
				showSuccessTick: true
			}))
		}

		dispatch({ type: SET_PROFILE_ICON_UPLOADING, data: false })
	} catch (error) {
		dispatch({ type: SET_PROFILE_ICON_UPLOADING, data: false })

		// Handle different error types
		let errorMessage = 'Failed to upload profile icon'

		if (error.name === 'AbortError') {
			errorMessage = 'Profile icon upload cancelled'
		} else if (error.response?.status === 400) {
			errorMessage = error.response?.data?.message || 'Invalid file or filename'
		} else if (error.response?.status === 401) {
			errorMessage = 'Authentication failed - please log in again'
		} else if (error.response?.status === 415) {
			errorMessage = 'Unsupported file type - please use .jpg, .png, .gif, .webp, or .svg'
		} else if (error.response?.status === 422) {
			errorMessage = 'Upload cancelled or timed out'
		} else if (error.response?.status === 500) {
			errorMessage = 'Server error during upload - please try again'
		} else if (error.message === 'Network Error') {
			errorMessage = 'Network error - please check your connection'
		}

		dispatch({ type: SET_PROFILE_ICON_ERROR, data: errorMessage })

		// Show error message
		dispatch(SetToast({
			title: errorMessage,
			show: true,
			showSuccessTick: false,
			showError: true
		}))
	}
}

/**
 * Cancel ongoing profile icon upload
 * @param {AbortController} abortController - The abort controller to cancel the upload
 */
export const cancelProfileIconUpload = (abortController) => dispatch => {
	if (abortController) {
		abortController.abort()
	}

	dispatch({
		type: SET_PROFILE_ICON_UPLOADING,
		data: false
	})

	dispatch(SetToast({
		title: 'Profile icon upload cancelled',
		show: true,
		showSuccessTick: false
	}))
}

export const removeProfileIcon = (profileId) => async (dispatch) => {
	try {
		const response = await profileService.removeProfileIcon(profileId)
		dispatch({ type: SET_PROFILE_ICON_URL, data: null })
		dispatch({ type: SET_PROFILE_ICON_UPLOADING, data: false })
		dispatch(SetToast({
			title: response.data.message || 'Profile icon removed successfully',
			show: true,
			showSuccessTick: true
		}))
	} catch {
		dispatch(SetToast({
			title: 'Failed to remove profile icon',
			show: true,
			showSuccessTick: false,
			showError: true
		}))
	}
}